import { type PluginListenerHandle, registerPlugin } from "@capacitor/core";
import type { MvActivityType } from "@/db/schema/movement";
import type { GPSPoint } from "@/lib/movement/gps-utils";

export type NativeMovementLocation = {
  id: number;
  accountId?: string;
  clientId?: string;
  segmentId?: number;
  latitude: number;
  longitude: number;
  altitude?: number | null;
  accuracy?: number | null;
  speed?: number | null;
  bearing?: number | null;
  time: number;
};

type HiiveMovementNotificationPlugin = {
  setAccount(input: { accountId: string | null }): Promise<void>;
  startTracking(input: {
    accountId: string;
    clientId: string;
    title: string;
    activityType: MvActivityType;
    startedAtMs: number;
    /** See HiiveMovementActivity.start. Ignored by older binaries. */
    gpsProfile?: "auto";
  }): Promise<void>;
  update(input: {
    controlAtMs: number;
    accountId: string;
    clientId: string;
    title: string;
    message: string;
    subText?: string;
    distanceMeters?: number;
    durationSeconds?: number;
    paceSecondsPerKm?: number;
    speedMps?: number;
    isPaused?: boolean;
  }): Promise<void>;
  clear(): Promise<void>;
  stopTracking(input?: {
    accountId: string;
    clientId: string;
    endedAtMs?: number;
  }): Promise<void>;
  getSession(input: {
    accountId: string;
    clientId: string;
  }): Promise<import("./hiive-movement-activity").NativeMovementSession>;
  getLocations(input: {
    accountId: string;
    clientId: string;
  }): Promise<{ locations: NativeMovementLocation[] }>;
  clearLocations(input: { accountId: string; clientId: string }): Promise<void>;
  addListener(
    event: "recordingError",
    callback: (
      error: import("./hiive-movement-activity").NativeMovementRecordingError,
    ) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "location",
    listener: (location: NativeMovementLocation) => void,
  ): Promise<PluginListenerHandle>;
};

export const HiiveMovementNotification =
  registerPlugin<HiiveMovementNotificationPlugin>("HiiveMovementNotification");

export function nativeMovementLocationToGPSPoint(
  location: NativeMovementLocation,
): GPSPoint | null {
  if (
    !Number.isFinite(location.latitude) ||
    !Number.isFinite(location.longitude) ||
    !Number.isFinite(location.time)
  ) {
    return null;
  }

  return {
    sampleId: `android:${location.id}:${location.time}`,
    segmentId: location.segmentId ?? 0,
    latitude: location.latitude,
    longitude: location.longitude,
    altitude:
      typeof location.altitude === "number" &&
      Number.isFinite(location.altitude)
        ? location.altitude
        : undefined,
    accuracy:
      typeof location.accuracy === "number" &&
      Number.isFinite(location.accuracy)
        ? location.accuracy
        : undefined,
    speed:
      typeof location.speed === "number" && Number.isFinite(location.speed)
        ? location.speed
        : undefined,
    heading:
      typeof location.bearing === "number" && Number.isFinite(location.bearing)
        ? location.bearing
        : undefined,
    timestamp: new Date(location.time).toISOString(),
  };
}
